import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { PnrSourceMark } from '@/components/provenance/pnr-source-mark';
import { cn } from '@/lib/cn';
import { careLaneReason, type CareDeskRow } from './care-desk-model';

// Counter capture bar (feat/care-desk K6, NEX-458) — the "passenger at the
// counter" fast path: one pinned input under the goal bar, `/` or ⌘K to focus,
// typeahead over the ALREADY-LOADED desk rows (pure client-side filter over
// pnrLabel / flight / masked names — the desk walk already holds the whole
// roster; no server search endpoint). Enter selects the row into the queue /
// resolve panel; a row in no open lane still opens read-only facts there.

const MAX_RESULTS = 8;

/** Case-insensitive match over the row's operator-visible identifiers. */
function matches(d: CareDeskRow, needle: string): boolean {
  const q = needle.toLowerCase();
  const { row } = d;
  if (row.pnrLabel.toLowerCase().includes(q)) return true;
  if (row.flightLabel?.toLowerCase().includes(q)) return true;
  if (row.flightNumber?.toLowerCase().includes(q)) return true;
  if (row.leadName?.toLowerCase().includes(q)) return true;
  return (row.passengers ?? []).some((p) => p.fullName?.toLowerCase().includes(q));
}

export function CareCounterBar({
  rows,
  onSelect,
}: {
  /** The FULL desk (every lane incl. settled) — a walked-up passenger may be all set. */
  rows: CareDeskRow[];
  onSelect: (subCaseUrn: string) => void;
}) {
  const { t } = useTranslation('care');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(blurTimer.current), []);

  const results = useMemo(() => {
    const q = query.trim();
    if (!q) return [];
    return rows.filter((d) => matches(d, q)).slice(0, MAX_RESULTS);
  }, [rows, query]);

  // `/` and ⌘K focus the bar — scoped to this tab's lifetime (the Care tab
  // unmounts when inactive). Never swallow keys while the operator types
  // elsewhere, and never touch anything else (undo/redo stay native).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      const isSlash = e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey;
      const isCmdK = e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey);
      if (!isSlash && !isCmdK) return;
      const target = e.target as HTMLElement | null;
      const typing =
        !!target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable);
      if (isSlash && typing) return; // '/' inside a field is just a character
      e.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const pick = (d: CareDeskRow): void => {
    onSelect(d.row.subCaseUrn);
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
  };

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = results[highlight] ?? results[0];
      if (target) pick(target);
    } else if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div className="relative shrink-0" data-testid="care-counter-bar">
      <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5">
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open && results.length > 0}
          aria-controls="care-counter-results"
          aria-label={t('counter.inputLabel')}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setHighlight(0);
            setOpen(true);
          }}
          onFocus={() => {
            clearTimeout(blurTimer.current);
            setOpen(true);
          }}
          onBlur={() => {
            // Delay so a mousedown on a result still lands before the popover goes.
            clearTimeout(blurTimer.current);
            blurTimer.current = setTimeout(() => setOpen(false), 150);
          }}
          onKeyDown={onInputKeyDown}
          placeholder={t('counter.placeholder')}
          className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          data-testid="care-counter-input"
        />
        <span className="hidden shrink-0 items-center gap-1 sm:flex" aria-hidden>
          <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            /
          </kbd>
          <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            ⌘K
          </kbd>
        </span>
      </div>

      {open && query.trim() !== '' ? (
        <ul
          id="care-counter-results"
          role="listbox"
          aria-label={t('counter.resultsLabel')}
          className="absolute inset-x-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-lg border border-border bg-popover p-1 shadow-md"
          data-testid="care-counter-results"
        >
          {results.length === 0 ? (
            <li className="px-2.5 py-2 text-sm text-muted-foreground" data-testid="care-counter-no-match">
              {t('counter.noMatch')}
            </li>
          ) : (
            results.map((d, i) => {
              const reason = careLaneReason(d);
              return (
                <li key={d.row.subCaseUrn} role="option" aria-selected={i === highlight}>
                  <button
                    type="button"
                    // mousedown beats the input blur — a click always lands.
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pick(d);
                    }}
                    onMouseEnter={() => setHighlight(i)}
                    className={cn(
                      'flex w-full items-baseline gap-2 rounded-md px-2.5 py-1.5 text-left text-sm',
                      i === highlight ? 'bg-muted' : 'hover:bg-muted/50',
                    )}
                    data-testid={`care-counter-result-${d.row.pnrLabel}`}
                  >
                    <span className="font-mono font-semibold">{d.row.pnrLabel}</span>
                    <PnrSourceMark
                      source={d.row.pnrSource}
                      className="shrink-0 self-center"
                      testId="care-counter-result-source"
                    />
                    <span className="text-xs text-muted-foreground">
                      {t('counter.paxFlight', {
                        pax: d.row.paxCount,
                        flight: d.row.flightLabel,
                      })}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-right text-xs text-muted-foreground">
                      {reason ? t(reason.key, { ...reason.params }) : t('counter.onDesk')}
                    </span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      ) : null}
    </div>
  );
}
